import { Component, Inject, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { SelectType } from '@core/enums/select';
import { AuthService } from '@core/services/auth.service';
import { ClassroomService } from '@core/services/classroom.service';
import { ToastService } from '@core/services/toast.service';
import { take, tap } from 'rxjs';
import { AgencyService } from '@core/services/agency.service';
import { Role } from '@core/enums/roles';
import { IAgency } from '@core/interfaces/iagency';
import { CustomValidators } from '@core/validators/custom.validator';
import { Modal } from '@core/enums/modal';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';
import { replaceWhitespace } from '@core/services/helper.service';
import { PermissionService } from '@core/services/permission.service';
import { Permission } from '@core/enums/permissions';
import { States } from '@core/enums/states';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'drdp-add-class-modal',
    templateUrl: './add-class-modal.component.html',
    styleUrls: ['./add-class-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddClassModalComponent implements OnInit {
  classroomForm: FormGroup | any;
  user = this.authService.getCurrentUser();
  options: any;
  label?: string;
  superUser?: boolean;
  payload: any = {};
  selectedState?: number | null;
  selectedAgency?: number | null;
  selectedSiteId?: number | null;
  clearDropdown = false;
  modal = inject(MatDialog);
  isProcessing: boolean = false;
  canViewDropdowns: boolean = this.permission.checkPermission(Permission.AccessAssignedStateAgencies) 
    || this.permission.checkPermission(Permission.AccessAllAgencies);
  states = States;
  isAgencyTemplateLocked = false;

  get selectType() {
    return SelectType;
  }
  get role() {
    return Role;
  }

  get name() {
    return this.classroomForm.get('name');
  }
  get siteId() {
    return this.classroomForm.get('siteId');
  }
  get agencyId() {
    return this.classroomForm.get('agencyId');
  }
  get stateId() {
    return this.classroomForm.get('stateId');
  }
  get classroomId() {
    return this.classroomForm.get('classroomId');
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public editData: any,
    public dialogRef: MatDialogRef<AddClassModalComponent>,
    private fb: FormBuilder,
    public toast: ToastService,
    private classroomService: ClassroomService,
    private authService: AuthService,
    private agency: AgencyService,
    private permission: PermissionService,
    private translocoService: TranslocoService
  ) {
    dialogRef.disableClose = true;
  }

  ngOnInit(): void {
    this.superUser = this.authService.isSuperUser();
    this.initializeForm();
    if (!this.editData) {
      this.selectedState = this.user.stateId;
      this.selectedAgency = this.user.agencyId;
      this.label = this.translocoService.translate('configuration.classroom.addTitle');
    } else {
      this.label = this.translocoService.translate('configuration.classroom.editTitle');
    }
  }

  initializeForm(): void {
    this.classroomForm = this.fb.group({
      name: [null, [Validators.required, Validators.maxLength(100), CustomValidators.cannotContainWhiteSpace]],
      siteId: [null, Validators.required],
      agencyId: [this.user.stateId, Validators.required],
      stateId: [this.user.agencyId, Validators.required],
    });

    if (this.editData) {
      const edit = this.editData.data;
      this.selectedState = edit.stateId;
      this.selectedAgency = edit.agencyId;
      this.selectedSiteId = edit.siteId;

      this.classroomForm.patchValue({
        name: edit.className,
        siteId: this.selectedSiteId,
        agencyId: this.selectedAgency,
        stateId: this.selectedState,
      });
    }
  }

  handleDropdown(data: any, type: string | number): void {
    if (type == this.selectType.States) {
      this.selectedState = data ? data.id : null;
      this.stateId.setValue(this.selectedState);

      if (this.selectedState) {
        this.agency
          .getAgenciesByState([this.selectedState])
          .pipe(
            take(1),
            tap((agency: IAgency[]) => {
              this.selectedAgency = agency[0] ? agency[0].id : null;
              this.agencyId.setValue(this.selectedAgency);
            }),
            tap((_) => {
              this.clearDropdown = true;
              this.selectedSiteId = null;
              this.stateId.setValue(this.selectedState);
            })
          )
          .subscribe();
      }
    } else if (type == this.selectType.Agencies) {
      this.selectedAgency = data ? data.id : null;
      this.isAgencyTemplateLocked = data?.isRatingViewLocked ?? data?.item.isRatingViewLocked;
      this.agencyId.setValue(this.selectedAgency);
    } else if (type == this.selectType.Sites) {
      this.selectedSiteId = data ? data.id ?? data[0].id : null;
      this.siteId.setValue(this.selectedSiteId);
    }
  }

  onSubmit(): any {
    if (this.isProcessing) return;
    if (this.classroomForm.invalid) {
      this.toast.error(this.translocoService.translate('configuration.common.requiredFieldsToast'));
      return;
    }
    this.name.setValue(replaceWhitespace(this.name.value));

    this.payload = {
      id: this.editData?.data.classId ?? 0,
      ...this.classroomForm.value,
      isSoftImplementation: this.editData?.data.isSoftImplementation ?? false,
    };

    this.isProcessing = true;
    this.classroomService
      .saveClassroom(this.payload)
      .pipe(take(1))
      .subscribe({
        next: (success) => {
          this.isProcessing = false;
          if (success) {
            this.dialogRef.close({ success: true });
            var event = { data: { modalInfo: {} } };
            event.data.modalInfo = {
              title: this.translocoService.translate('configuration.classroom.savedTitle'),
              message: this.translocoService.translate('configuration.classroom.savedMessage'),
              primaryBtnClass: 'blue',
              showCancel: false,
              primaryBtnText: Modal.OK
            };
            this.modal.open(ConfirmationModalComponent, { data: event ? event : undefined });
          }
        },
        error: (err) => {
          this.isProcessing = false;
        }
      });
  }
}
